import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar } from "../shared/Avatar";
import { PhotoUploader } from "../shared/PhotoUploader";
import { InfoItem } from "../shared/InfoItem";
import { CustomIcon } from "../shared/CustomIcon";
import { fullName } from "../../lib/format";
import AvailabilityEditor from "./AvailabilityEditor";
import { useTheme } from "../../lib/ThemeContext";
import { kcFetch, API_BASE, authHeaders } from "../../lib/apiClient";
import MemberPrivacyEditor from "../shared/MemberPrivacyEditor";

async function apiUpdateMyProfile(payload) {
  const res = await kcFetch(`${API_BASE}/me/profile`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Update failed");
  return res.json();
}

async function apiUpdateMyMemberPrivacy(privacy) {
  const res = await kcFetch(`${API_BASE}/me/member-privacy`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ privacy }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Privacy update failed");
  return res.json();
}

// ============================================================
// MY PROFILE — the current user's own profile page (linked from sidebar).
// Extracted from KingdomConnect.jsx (final extraction wave). Pure UI move
// — no functional changes.
// ============================================================
function roleLabel(role) {
  if (role === "superadmin")    return "Super Admin";
  if (role === "limited_admin") return "Limited Admin";
  if (role === "pastor")        return "Pastor";
  return "Team Member";
}
function roleBadgeClass(role) {
  if (role === "superadmin")    return "badge-gold";
  if (role === "limited_admin") return "badge-blue";
  if (role === "pastor")        return "badge-green";
  return "badge-charcoal";
}

export default function MyProfilePage({ currentUser, setCurrentUser, users, setUsers, members, setMembers, persist }) {
  const reactNav = useNavigate();
  const { theme, setTheme } = useTheme();
  const me = users.find(u => u.id === currentUser.id) || currentUser;
  const linkedMember = me.memberId ? members.find(m => m.id === me.memberId) : null;
  const assigned = (me.assignedMembers || []).map(id => members.find(m => m.id === id)).filter(Boolean);
  const [editing, setEditing] = useState(false);
  const [photo, setPhoto] = useState(me.photo || null);
  const [name, setName] = useState(me.name || "");
  const [email, setEmail] = useState(me.email || "");
  const [privacy, setPrivacy] = useState(() => (linkedMember && linkedMember.privacy) || {});
  // Re-sync the local privacy state whenever the linked member's privacy
  // changes (e.g. when the async kc_members fetch finishes after first
  // render, or when an admin edits the same record concurrently). Without
  // this, useState's lazy initializer captures {} on first paint and the
  // saved server-side flags never appear on screen. Iter33 RCA.
  useEffect(() => {
    setPrivacy(linkedMember?.privacy || {});
  }, [linkedMember, linkedMember?.privacy]);
  const [privacyStatus, setPrivacyStatus] = useState(""); // transient banner
  const [privacySaving, setPrivacySaving] = useState(false);

  const savePrivacy = async (next) => {
    setPrivacy(next);
    setPrivacySaving(true);
    setPrivacyStatus("");
    try {
      const fresh = await apiUpdateMyMemberPrivacy(next);
      // Mirror onto local members state so the profile view & dashboards
      // reflect the change immediately without a re-fetch.
      if (typeof setMembers === "function" && fresh && fresh.id) {
        setMembers(prev => prev.map(m => m.id === fresh.id ? { ...m, ...fresh } : m));
      }
      setPrivacyStatus("Privacy preferences saved.");
      setTimeout(() => setPrivacyStatus(""), 2200);
    } catch (err) {
      setPrivacyStatus(`Could not save: ${err.message || err}`);
    }
    setPrivacySaving(false);
  };

  const save = async () => {
    try {
      const fresh = await apiUpdateMyProfile({ name, email, photo });
      const updated = users.map(u => u.id === me.id ? { ...u, ...fresh } : u);
      setUsers(updated);
      setCurrentUser({ ...currentUser, name, email, photo });
      setEditing(false);
    } catch (err) {
      alert(`Save failed: ${err.message || err}`);
    }
  };

  const myRoleLabel = roleLabel(me.role);
  const myRoleBadge = roleBadgeClass(me.role);

  return (
    <div data-testid="my-profile-page">
      <div className="profile-hero">
        <Avatar className="profile-avatar-lg" name={me.name} photo={me.photo} memberId={me.memberId} />
        <div className="profile-hero-body">
          <div className="profile-name">{me.name}</div>
          <div className="profile-meta">
            <span className={`badge ${myRoleBadge}`} style={{ marginRight: 8 }}>{myRoleLabel}</span>
            <span style={{ fontFamily: "monospace", fontSize: 12, color: "var(--kc-accent)", wordBreak: "break-all" }}>@{me.username}</span>
          </div>
        </div>
        <div className="profile-hero-actions">
          <button className="btn btn-outline" data-testid="my-profile-edit" onClick={() => setEditing(true)} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><CustomIcon name="edit" size={14} /> Edit Profile</button>
        </div>
      </div>

      <div className="card mb-20">
        <div className="card-header"><div className="card-title">Account Details</div></div>
        <div style={{ padding: "18px 24px" }}>
          <div className="form-grid">
            <InfoItem label="Username" value={me.username} />
            <InfoItem label="Email" value={me.email} />
            <InfoItem label="Role" value={myRoleLabel} />
            <InfoItem label="Session Timeout" value={`${me.sessionTimeout || 30} minutes`} />
            {linkedMember && (
              <InfoItem
                label="Linked Member Record"
                value={<button className="btn btn-sm btn-outline" onClick={() => reactNav(`/members/${linkedMember.id}`)}>{fullName(linkedMember)} →</button>}
              />
            )}
          </div>
        </div>
      </div>

      <div className="card mb-20">
        <div className="card-header"><div className="card-title">Appearance</div></div>
        <div style={{ padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div style={{ maxWidth: 480 }}>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: "var(--text2, rgba(255,255,255,0.7))", lineHeight: 1.5 }}>
              Choose between the original dark theme and a soft light theme. Your selection is saved on your account and applied across every device you sign in from.
            </div>
            <div style={{ marginTop: 6, fontSize: 11, color: "var(--text3, rgba(255,255,255,0.5))" }}>
              Current: <strong data-testid="theme-current">{theme === "light" ? "Light Mode" : "Dark Mode"}</strong>
            </div>
          </div>
          <div style={{ display: "inline-flex", gap: 8 }}>
            <button
              type="button"
              data-testid="theme-toggle-dark"
              className={`btn btn-sm ${theme === "dark" ? "btn-gold" : "btn-outline"}`}
              onClick={() => setTheme("dark")}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            ><CustomIcon name="day-night" size={14} /> Dark</button>
            <button
              type="button"
              data-testid="theme-toggle-light"
              className={`btn btn-sm ${theme === "light" ? "btn-gold" : "btn-outline"}`}
              onClick={() => setTheme("light")}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            ><CustomIcon name="day-night" size={14} /> Light</button>
          </div>
        </div>
      </div>

      {/* Pastors and admins receive bookings from the public QR poster.
          Letting them set working hours stops visitors from booking
          outside their available windows. */}
      {(me.role === "pastor" || me.role === "superadmin" || me.role === "limited_admin") && (
        <AvailabilityEditor
          currentUser={currentUser}
          setCurrentUser={setCurrentUser}
          users={users}
          setUsers={setUsers}
        />
      )}

      {/* Privacy controls — only when the user has a linked member record. */}
      {linkedMember && (
        <div className="card mb-20" data-testid="my-profile-privacy-card">
          <div className="card-header"><div className="card-title">Privacy</div></div>
          <div style={{ padding: "18px 24px" }}>
            <MemberPrivacyEditor
              value={privacy}
              onChange={savePrivacy}
              isAdmin={false}
              canEdit={true}
              testIdPrefix="my-profile-privacy"
            />
            <div
              data-testid="my-profile-privacy-status"
              aria-live="polite"
              style={{
                marginTop: 10, minHeight: 18,
                fontFamily: "'Montserrat', sans-serif", fontSize: 12,
                color: privacyStatus.startsWith("Could not") ? "#ff9b6b" : "rgba(74,222,128,0.95)",
              }}
            >{privacySaving ? "Saving…" : privacyStatus}</div>
          </div>
        </div>
      )}

      {(me.role === "team_member" || me.role === "pastor") && (
        <div className="card mb-20">
          <div className="card-header"><div className="card-title">My Assigned Members ({assigned.length})</div></div>          {assigned.length === 0 ? (
            <div className="empty"><div className="empty-text">No members assigned yet.</div></div>
          ) : (
            <div>
              {assigned.map(m => (
                <div key={m.id} className="dash-row-link" role="link" tabIndex={0}
                  onClick={() => reactNav(`/members/${m.id}`)}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)", cursor: "pointer" }}>
                  <Avatar name={fullName(m)} photo={m.photo} memberId={m.id} status={m.memberStatus} style={{ fontSize: 11 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 14, fontWeight: 500, color: "var(--kc-text)" }}>{fullName(m)}</div>
                    <div style={{ fontSize: 11, color: "var(--kc-meta)" }}>{m.memberStatus} · {m.phone?.[0]?.number || "—"}</div>
                  </div>
                  <span style={{ color: "var(--kc-meta)" }}>›</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {editing && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEditing(false)}>
          <div className="modal" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <div className="modal-title">Edit My Profile</div>
              <button className="modal-close" onClick={() => setEditing(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group full mb-14">
                <label>Profile Photo</label>
                <PhotoUploader value={photo} name={name} onChange={setPhoto} data-testid="my-profile-photo" />
              </div>
              <div className="form-group mb-14"><label>Full Name</label><input value={name} onChange={e => setName(e.target.value)} data-testid="my-profile-name" /></div>
              <div className="form-group mb-14"><label>Email</label><input type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="my-profile-email" /></div>
              <div className="form-group mb-14">
                <label style={{ color: "var(--kc-meta)" }}>Need to change your password?</label>
                <div style={{ fontSize: 12, color: "var(--kc-meta)", lineHeight: 1.5, fontFamily: "'Montserrat',sans-serif" }}>
                  Please contact your Super Admin — they can reset it for you in Users &amp; Roles.
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setEditing(false)}>Cancel</button>
              <button className="btn btn-gold" data-testid="my-profile-save" onClick={save}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
